import { Component, ReactNode, useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import useEmblaCarousel from 'embla-carousel-react';
import * as Breakpoints from '../../../constants/breakpoints';
import * as InternalPropTypes from '../../../constants/internal-types';
import { TypeQuoteTypes } from '../../../constants/cms-constants';
import Quote from './Quote';
import Hstack3CellExtremity from '../atoms/Hstack3CellExtremity';
import IndexSpinner from '../atoms/IndexSpinner';
import Hstack2Cell from '../atoms/Hstack2Cell';
import ArrowButton from '../atoms/ArrowButton';
import Caption from '../atoms/Caption';

const styles = css({
  '& .caption': {
    opacity: '0.4',
  },
  [Breakpoints.DesktopUp.mq]: {
    paddingTop: '100px',
    paddingBottom: '100px',
  },
  [Breakpoints.Tablet.mq]: {
    paddingTop: '150px',
    paddingBottom: '40px',
  },
  [Breakpoints.Mobile.mq]: {
    paddingTop: '85px',
    paddingBottom: '30px',
  },
  '& .carousel': {
    overflow: 'hidden',
    [Breakpoints.DesktopUp.mq]: {
      height: '365px',
    },
    [Breakpoints.Tablet.mq]: {
      height: '465px',
    },
    [Breakpoints.Mobile.mq]: {
      height: '250px',
    },
  },
  '& .buttons': {
    display: 'flex',
    height: '100%',
    alignItems: 'flex-end',
    '& button': {
      marginLeft: '8px',
    },
  },
  '.embla__viewport': {
    overflow: 'hidden',
    [Breakpoints.Mobile.mq]: {
      marginBottom: '30px',
    },
    [Breakpoints.Tablet.mq]: {
      marginBottom: '45px',
    },
    [Breakpoints.DesktopUp.mq]: {
      marginBottom: '45px',
    },
  },
  '.embla__container': {
    backfaceVisibility: 'hidden',
    display: 'flex',
    alignItems: 'center',
    touchAction: 'pan-x',
    flexDirection: 'column',
    [Breakpoints.Mobile.mq]: {
      height: '220px',
      width: '100%',
    },
    [Breakpoints.Tablet.mq]: {
      height: '420px',
      width: '100%',
    },
    [Breakpoints.DesktopUp.mq]: {
      height: '320px',
      width: '100%',
    },
  },
  '.embla__slide': {
    flex: '0 0 100%',
  },
});

type QuoteCarouselProps = {
  lightHeaderText: InternalPropTypes.RichText;
  quotes: InternalPropTypes.MediaItem[];
  type: TypeQuoteTypes;
};

function QuoteCarousel(props: QuoteCarouselProps) {
  const { lightHeaderText, quotes, type } = props;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    axis: 'y',
  });

  const [currentIndex, setCurrentIndex] = useState(0);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onScroll = useCallback((emblaApi) => {
    const index = emblaApi.selectedScrollSnap() + 1;
    setCurrentIndex(index);
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onScroll(emblaApi);
    emblaApi.on('reInit', onScroll);
    emblaApi.on('scroll', onScroll);
  }, [emblaApi, onScroll]);

  return (
    <div css={styles}>
      {lightHeaderText && (
        <Caption type={Caption.types.SECTION}>{lightHeaderText}</Caption>
      )}
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {
            // eslint-disable-next-line react/prop-types
            quotes.map((quote) => (
              <div className="embla__slide" key={quote.name}>
                <Quote
                  quote={quote.header}
                  sender={quote.sender.name}
                  type={type}
                />
              </div>
            ))
          }
        </div>
      </div>
      <Hstack3CellExtremity
        onRenderLeftCell={() => (
          <div>
            <IndexSpinner
              currentIndex={currentIndex}
              maxIndex={quotes.length}
            />
          </div>
        )}
        onRenderRightCell={() => (
          <Hstack2Cell
            className="buttons"
            onRenderLeftCell={() => (
              <ArrowButton onClick={onPrevButtonClick} direction="left" />
            )}
            onRenderRightCell={() => (
              <ArrowButton onClick={onNextButtonClick} />
            )}
          />
        )}
      />
    </div>
  );
}

export default QuoteCarousel;
